// frontend/src/pages/AdmissionPage.js
import { useState, useEffect } from 'react';
import { Button, Form, Input, DatePicker, Select, Typography, Card, Row, Col, Divider, Checkbox, Result, Tabs, Upload, message as antdMessage } from 'antd';
import { useNavigate } from 'react-router-dom';
import ImgCrop from 'antd-img-crop';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { submitInquiry, getPublicClasses, checkAdmissionNo } from '../services/inquiryService';
import { useNotification } from '../context/NotificationContext';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const AdmissionPage = () => {
    const { message } = useNotification();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [activeTab, setActiveTab] = useState('1');
    const [declarationChecked, setDeclarationChecked] = useState(false);
    const [classes, setClasses] = useState([]);
    const [fileList, setFileList] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        getPublicClasses().then(data => {
            setClasses(data.map(c => ({ value: c.id, label: c.name })));
        }).catch(err => {
            console.error('Failed to load classes', err);
        });
    }, []);

    const gradeOptions = [
        { value: 'A+', label: 'A+' },
        { value: 'A', label: 'A' },
        { value: 'B+', label: 'B+' },
        { value: 'B', label: 'B' },
        { value: 'C+', label: 'C+' },
        { value: 'C', label: 'C' },
        { value: 'D+', label: 'D+' },
        { value: 'D', label: 'D' },
        { value: 'E', label: 'E' },
    ];

    // --- render helper + tab/field configuration ---
    const renderField = (field) => {
        if (field.render) {
            return (
                <Col {...(field.colProps || { xs: 24, md: 12, lg: 8 })} key={field.name}>
                    {field.render(field)}
                </Col>
            );
        }
        const common = { 
            name: field.name, 
            label: field.label, 
            rules: field.rules || [], 
            valuePropName: field.valuePropName || undefined,
            extra: field.extra
        };
        const colProps = field.colProps || { xs: 24, md: 12, lg: field.type === 'textarea' ? 24 : 8 };
        let input = null;

        switch (field.type) {
            case 'date':
                input = <DatePicker
                    style={{ width: '100%' }}
                    disabledDate={date => date && date > dayjs()}
                />;
                break;
            case 'select':
                input = <Select placeholder={`Select ${field.label}`}>{(field.options || []).map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}</Select>;
                break;
            case 'textarea':
                input = <Input.TextArea 
                    rows={field.rows || 2} 
                    style={['address'].includes(field.name) ? { textTransform: 'uppercase' } : {}}
                    onInput={['address'].includes(field.name) ? (e) => { e.target.value = e.target.value.toUpperCase(); } : undefined}
                />;
                break;
            case 'checkbox':
                input = <Checkbox onChange={field.onChange}>{field.checkboxLabel}</Checkbox>;
                break;
            case 'upload':
                input = (
                    <ImgCrop 
                        rotationSlider 
                        showReset
                        quality={1}
                        aspect={3.5 / 4.5} 
                        modalTitle="Crop Student Photo"
                        modalOk="Confirm Crop"
                    >
                        <Upload
                            listType="picture-card"
                            maxCount={1}
                            fileList={fileList}
                            beforeUpload={(file) => {
                                // This prevents automatic upload and keeps the cropped file in state
                                setFileList([file]);
                                return false;
                            }}
                            onRemove={() => setFileList([])}
                        >
                            {fileList.length < 1 && (
                                <div>
                                    <UploadOutlined />
                                    <div style={{ marginTop: 8 }}>Upload Photo</div>
                                </div>
                            )}
                        </Upload>
                    </ImgCrop>
                );
                break;
            default:
                input = <Input 
                    placeholder={`Enter ${field.label}`} 
                    style={['studentName', 'parentName', 'mother_name', 'guardian_name'].includes(field.name) ? { textTransform: 'uppercase' } : {}}
                    onInput={['studentName', 'parentName', 'mother_name', 'guardian_name'].includes(field.name) ? (e) => { e.target.value = e.target.value.toUpperCase(); } : undefined}
                />;
        }

        return (
            <Col {...colProps} key={field.name}>
                <Form.Item {...common}>
                    {input}
                </Form.Item>
            </Col>
        );
    };

    const tabStructure = [
        {
            key: '1', tab: 'Basic & Academic', sections: [
                {
                    title: 'Basic Information', fields: [
                        { name: 'studentName', label: 'Name', rules: [{ required: true }] },
                        {
                            name: 'admission_no', label: 'Admission Number', rules: [
                                { required: true },
                                {
                                    validator: async (_, value) => {
                                        if (!value) return Promise.resolve();
                                        try {
                                            const res = await checkAdmissionNo(value);
                                            if (res.exists) return Promise.reject('Admission Number already exists');
                                            return Promise.resolve();
                                        } catch (e) {
                                            return Promise.resolve(); // If API fails, don't block
                                        }
                                    }
                                }
                            ]
                        },
                        { name: 'class_id', label: 'Class', type: 'select', options: classes, rules: [{ required: true }] },
                        { name: 'dob', label: 'Date of birth', type: 'date', rules: [{ required: true }] },
                        { name: 'gender', label: 'Gender', type: 'select', options: [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }], rules: [{ required: true }] },
                        { name: 'allotted_category', label: 'Category (Poverty Status)', type: 'select', options: [{ value: 'General', label: 'General' }, { value: 'SC', label: 'SC' }, { value: 'ST', label: 'ST' }, { value: 'OBC', label: 'OBC' }], rules: [{ required: true }] },
                        { name: 'phone_number', label: 'Phone Number', rules: [{ required: true }] },
                        { 
                            name: 'photo', 
                            label: 'Student Photo', 
                            type: 'upload', 
                            extra: 'Upload any clear photo of the student. You can crop and rotate it in the next step to fit passport dimensions.'
                        },
                        { name: 'address', label: 'Address', type: 'textarea', rules: [{ required: true }] },
                    ]
                },
                {
                    title: 'SSLC Grades', fields: [
                        { name: 'sslc_maths', label: 'Maths', type: 'select', options: gradeOptions, rules: [{ required: true }] },
                        { name: 'sslc_physics', label: 'Physics', type: 'select', options: gradeOptions, rules: [{ required: true }] },
                        { name: 'sslc_chemistry', label: 'Chemistry', type: 'select', options: gradeOptions, rules: [{ required: true }] },
                        { name: 'sslc_biology', label: 'Biology', type: 'select', options: gradeOptions, rules: [{ required: true }] },
                        { name: 'sslc_social_studies', label: 'Social Studies', type: 'select', options: gradeOptions, rules: [{ required: true }] },
                        { name: 'sslc_english', label: 'English', type: 'select', options: gradeOptions, rules: [{ required: true }] },
                    ]
                },
                {
                    title: 'Previous Academic Information', fields: [
                        { name: 'sslc_reg_no', label: 'SSLC Reg. No', rules: [{ required: true }] },
                        { name: 'sslc_school', label: 'SSLC School', rules: [{ required: true }] },
                    ]
                }
            ]
        },
        {
            key: '2', tab: 'Family Details', sections: [
                {
                    fields: [
                        { name: 'parentName', label: 'Name of father', rules: [{ required: true }] },
                        { name: 'father_occupation', label: 'Occupation of father', rules: [{ required: true }] },
                        { name: 'father_qualification', label: 'Edu. qualification', rules: [{ required: true }] },
                        { name: 'father_is_alive', label: 'Father Alive/Not', type: 'select', options: [{ value: 'true', label: 'Alive' }, { value: 'false', label: 'Not Alive' }], rules: [{ required: true }] },
                    ]
                },
                {
                    fields: [
                        { name: 'mother_name', label: 'Name of mother', rules: [{ required: true }] },
                        { name: 'mother_occupation', label: 'Occupation of Mother', rules: [{ required: true }] },
                        { name: 'mother_qualification', label: 'Edu. qualification', rules: [{ required: true }] },
                        { name: 'mother_is_alive', label: 'Mother Alive/Not', type: 'select', options: [{ value: 'true', label: 'Alive' }, { value: 'false', label: 'Not Alive' }], rules: [{ required: true }] },
                    ]
                },
                {
                    fields: [
                        { name: 'guardian_name', label: 'Name of Guardian', rules: [{ required: true }] },
                        { name: 'guardian_phone_number', label: 'Phone Number of Guardian', rules: [{ required: true }] },
                    ]
                }
            ]
        },
        {
            key: '3', tab: 'Other Information', sections: [
                {
                    fields: [
                        { name: 'religion_caste', label: 'Religion & Caste', rules: [{ required: true }] },
                        { name: 'ration_card_type', label: 'Ration card (APL/BPL)', type: 'select', options: [{ value: 'APL', label: 'APL' }, { value: 'BPL', label: 'BPL' }], rules: [{ required: true }] },
                        {
                            name: 'aadhar_no',
                            label: 'Aadhar No',
                            rules: [
                                { required: true },
                                { pattern: /^\d{12}$/, message: 'Aadhar number must be exactly 12 digits' }
                            ]
                        },
                        { name: 'blood_group', label: 'Blood group', rules: [{ required: true }] },
                        { name: 'sws_no', label: 'SWS No', rules: [{ required: true }] },
                    ]
                },
                {
                    title: 'Bank Details', fields: [
                        { name: 'bank_name', label: 'Name of Bank' },
                        { name: 'bank_account_no', label: 'Bank A/C No' },
                        { name: 'ifsc_code', label: 'IFSC Code' },
                        { name: 'scholarship_received', label: 'Scholarship Received', type: 'checkbox', valuePropName: 'checked' },
                    ]
                }
            ]
        },
        {
            key: '4', tab: 'Extra-Curricular & Health', sections: [
                {
                    title: 'Arts', fields: [
                        { name: 'participated_in_arts', label: 'Participated in arts (YES/NO)', type: 'checkbox', valuePropName: 'checked' },
                        { name: 'arts_items', label: 'Items Participated', type: 'textarea' },
                        { name: 'arts_prizes', label: 'Places/Prizes obtained', type: 'textarea' },
                    ]
                },
                {
                    title: 'Sports', fields: [
                        { name: 'participated_in_sports', label: 'Participation in sports (YES/NO)', type: 'checkbox', valuePropName: 'checked' },
                        { name: 'sports_items', label: 'Items Participated', type: 'textarea' },
                        { name: 'sports_prizes', label: 'Places/Prizes obtained', type: 'textarea' },
                    ]
                },
                {
                    title: 'Health', fields: [
                        { name: 'chronic_illnesses', label: 'Any chronic patients in family', type: 'textarea' },
                        { name: 'disease', label: 'Disease', type: 'textarea' },
                    ]
                }
            ]
        }
    ];

    // generate field->tab mapping dynamically
    const fieldToTabMapping = {};
    tabStructure.forEach(tab => tab.sections.forEach(sec => sec.fields.forEach(f => { fieldToTabMapping[f.name] = tab.key; })));
    fieldToTabMapping.declaration = '5';

    const getFieldNamesByTab = (key) => {
        if (key === '5') return ['declaration'];
        const tab = tabStructure.find(t => t.key === key);
        if (!tab) return [];
        const names = [];
        tab.sections.forEach(section => {
            section.fields.forEach(f => {
                if (f.name) names.push(f.name);
            });
        });
        return names;
    };

    const handleNextForTab = async (key) => {
        const names = getFieldNamesByTab(key);
        try {
            await form.validateFields(names);
            const allKeys = [...tabStructure.map(t => t.key), '5'];
            const idx = allKeys.indexOf(key);
            const next = allKeys[idx + 1];
            if (next) setActiveTab(next);
        } catch (err) {
            message.error('Please fix errors before proceeding.');
        }
    };

    const handleSaveAndSubmit = async () => {
        try {
            await form.validateFields();
            form.submit();
        } catch (err) {
            message.error('Please fix errors before saving.');
        }
    };

    const onFinishFailed = ({ errorFields }) => {
        if (errorFields.length > 0) {
            const firstErrorFieldName = errorFields[0].name[0];
            const targetTab = fieldToTabMapping[firstErrorFieldName];

            if (targetTab && targetTab !== activeTab) {
                setActiveTab(targetTab);
                antdMessage.error(`Validation failed in tab: ${tabStructure.find(t => t.key === targetTab)?.tab || 'Confirmation'}`);
            }
        }
    };

    const onFinish = (values) => {
        setSaving(true);

        const formData = new FormData();

        Object.keys(values).forEach(key => {
            if (key === 'declaration') return;
            if (key === 'photo') return;

            let val = values[key];
            if (val && dayjs.isDayjs(val)) {
                val = val.format('YYYY-MM-DD');
            }
            if (val !== undefined && val !== null) {
                formData.append(key, val);
            }
        });

        // Retrieve the cropped file (which was saved via beforeUpload)
        if (fileList.length > 0) {
            const file = fileList[0];
            // If the file was cropped, the blob is either the file itself or in originFileObj
            formData.append('photo', file.originFileObj || file);
        }

        submitInquiry(formData)
            .then((response) => {
                setSubmitted(true);
                message.success(response.message || 'Application submitted successfully!');
            })
            .catch(error => {
                console.error(error);
                message.error(error.response?.data?.error || 'Failed to submit application.');
            })
            .finally(() => {
                setSaving(false);
            });
    };

    if (submitted) {
        return (
            <Result
                status="success"
                title="Application Submitted Successfully!"
                subTitle="The school administration will review your application and contact you shortly. Thank you."
                extra={[
                    <Button type="primary" key="home" onClick={() => window.location.reload()}>
                        Submit Another Application
                    </Button>,
                ]}
            />
        );
    }

    return (
        <div style={{ background: '#f0f2f5', minHeight: '100vh', padding: '16px' }}>
            <Card style={{ maxWidth: '1000px', margin: '0 auto', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <Title level={2} style={{ color: '#1890ff', marginBottom: 8 }}>KALLADI ABDU HAJI HIGHER SECONDARY SCHOOL</Title>
                    <Title level={4} style={{ margin: 0, fontWeight: 400 }}>Online Admission Application</Title>
                </div>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
                >
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        type="line"
                        size="large"
                    >
                        {tabStructure.map(tab => (
                            <TabPane tab={tab.tab} key={tab.key}>
                                <div style={{ padding: '16px 0' }}>
                                    {tab.sections.map((sec, idx) => (
                                        <div key={idx} style={{ marginBottom: 24 }}>
                                            {sec.title && <Title level={5} style={{ borderLeft: '4px solid #1890ff', paddingLeft: 12, marginBottom: 20 }}>{sec.title}</Title>}
                                            <Row gutter={[24, 0]}>
                                                {sec.fields.map(renderField)}
                                            </Row>
                                            {idx < tab.sections.length - 1 && <Divider style={{ margin: '12px 0 24px' }} />}
                                        </div>
                                    ))}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, gap: 12 }}>
                                        {parseInt(tab.key) > 1 && (
                                            <Button onClick={() => setActiveTab(String(parseInt(tab.key) - 1))}>
                                                Previous
                                            </Button>
                                        )}
                                        <Button type="primary" onClick={() => handleNextForTab(tab.key)}>
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            </TabPane>
                        ))}

                        <TabPane tab="Confirmation" key="5">
                            <Card bordered={false} style={{ backgroundColor: '#F8FAFC', borderRadius: '12px' }}>
                                <Title level={4} style={{ fontWeight: 700, color: '#1E293B' }}>Declaration of Accuracy</Title>
                                <Paragraph style={{ fontSize: '15px', lineHeight: '1.7', color: '#475569' }}>
                                    I hereby solemnly declare that all the information provided in this online application form is true, complete, and accurate to the best of my knowledge and belief. I understand that the discovery of any false information or significant omission will render this application liable to immediate rejection or subsequent dismissal if already admitted. 
                                </Paragraph>
                                <Paragraph style={{ fontSize: '15px', lineHeight: '1.7', color: '#475569' }}>
                                    By proceeding with this submission, I agree to abide by the rules, regulations, and values of <strong>Kalladi Abdu Haji Higher Secondary School</strong>.
                                </Paragraph>
                                
                                <Divider style={{ margin: '24px 0' }} />

                                <Form.Item
                                    name="declaration"
                                    valuePropName="checked"
                                    rules={[{
                                        validator: (_, value) =>
                                            value ? Promise.resolve() : Promise.reject(new Error('Acknowledgment of declaration is required to proceed.'))
                                    }]}
                                >
                                    <Checkbox onChange={(e) => setDeclarationChecked(e.target.checked)}>
                                        <Text strong style={{ fontSize: '15px' }}>I confirm that the provided information is accurate and I agree to the terms.</Text>
                                    </Checkbox>
                                </Form.Item>
                                
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 32, gap: 12 }}>
                                    <Button size="large" onClick={() => setActiveTab('4')}>
                                        Review Details
                                    </Button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={handleSaveAndSubmit}
                                        loading={saving}
                                        disabled={!declarationChecked}
                                        style={{ minWidth: 200, height: '48px', borderRadius: '8px', fontWeight: 600 }}
                                    >
                                        Finalize & Submit
                                    </Button>
                                </div>
                            </Card>
                        </TabPane>
                    </Tabs>
                </Form>
            </Card>
        </div>
    );
};

export default AdmissionPage;